/**
 * Did the action the operator just created actually become a button?
 *
 * Creating an action writes an `action_catalogs` row, but the monitoring
 * control panel — and this step's own table — render exclusively from
 * `action_banks`. The server materializes one from the other inside the create
 * request, and that materialization can fail on its own: the catalog row is
 * kept deliberately, so the operator's choice survives, and the response is
 * still a 201.
 *
 * Before the server reported it, a create whose materialization failed was
 * indistinguishable from one that succeeded. The step re-reads the bank after
 * every write, so the operator watched the row they just created fail to
 * appear, with no error anywhere. This module turns that silence into a
 * sentence.
 */

/** The server's verdict on whether the backing action_banks row was written. */
export type MaterializationState = "materialized" | "not_applicable" | "failed";

export interface MaterializationStatus {
  state: MaterializationState;
  reason?: string;
}

/** One row's outcome inside a batch create, keyed by its catalog id. */
export interface BatchMaterializationStatus extends MaterializationStatus {
  catalog_id: string;
}

/**
 * Read the materialization verdict off a create response.
 *
 * Returns `undefined` when the field is absent, which is how an older server
 * answers. Absent is deliberately NOT treated as failure: this frontend is
 * deployed against servers that predate the field, and warning on every create
 * there would train operators to ignore the warning — the one outcome worse
 * than not having it. Callers fall back to checking whether the row came back.
 */
export function readMaterializationStatus(response: unknown): MaterializationStatus | undefined {
  const raw = (response as { materialization?: unknown })?.materialization;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;

  const { state, reason } = raw as { state?: unknown; reason?: unknown };
  if (state !== "materialized" && state !== "not_applicable" && state !== "failed") {
    return undefined;
  }

  return { state, reason: typeof reason === "string" && reason ? reason : undefined };
}

/** Read the per-row verdicts off a batch create response. */
export function readBatchMaterializationStatuses(response: unknown): BatchMaterializationStatus[] {
  const raw = (response as { materialization?: unknown })?.materialization;
  if (!Array.isArray(raw)) return [];

  return raw.reduce<BatchMaterializationStatus[]>((out, entry) => {
    const status = readMaterializationStatus({ materialization: entry });
    const catalogId = (entry as { catalog_id?: unknown })?.catalog_id;
    if (status && typeof catalogId === "string" && catalogId) {
      out.push({ ...status, catalog_id: catalogId });
    }
    return out;
  }, []);
}

/**
 * Whether a verdict means a button will render.
 *
 * `not_applicable` counts as NOT rendered. It means the catalog carried no bank
 * scope, which for a wizard-created action is itself wrong — the wizard always
 * sends one — so an operator seeing it needs to know as much as they would for
 * an outright failure.
 */
export function willRender(status: MaterializationStatus | undefined): boolean {
  return status?.state === "materialized";
}

/**
 * The sentence shown to the operator when their action did not become a button.
 *
 * Names the bank, because the cross-bank add posts to many and "it failed" is
 * useless when the operator has to work out which of fourteen to retry. Carries
 * the server's reason when there is one, since the causes are distinguishable
 * and actionable — a command the controller cannot resolve is a different fix
 * from a database write that failed.
 */
export function materializationWarning(
  bankName: string,
  status: MaterializationStatus | undefined,
): string {
  const where = bankName ? `on ${bankName}` : "on this bank";
  const because = status?.reason ? ` (${status.reason})` : "";

  if (status?.state === "not_applicable") {
    return (
      `The action was saved ${where} but has no bank binding, so no button will appear ` +
      `for it${because}. Re-add it with a bank selected.`
    );
  }

  return (
    `The action was saved ${where} but its button was not created, so it will not appear ` +
    `in monitoring${because}. Try adding it again; if it keeps failing, the command may not ` +
    `be one this bank's controller accepts.`
  );
}

/**
 * Names the banks in a batch whose rows will not render.
 *
 * Returns an empty array when every row landed, so the caller's check is
 * `length > 0` rather than a truthiness test on a possibly-absent field.
 */
export function unrenderedCatalogIds(statuses: BatchMaterializationStatus[]): string[] {
  return statuses.filter((entry) => !willRender(entry)).map((entry) => entry.catalog_id);
}
